import { useEffect, useRef, useState, type ElementType, type ReactNode } from "react";
import { clsx } from "clsx";

interface RevealProps {
  children: ReactNode;
  /** Render element (default div). */
  as?: ElementType;
  /** Stagger delay in ms before the rise animation runs. */
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Scroll-entrance wrapper matching the design's `.rv` behaviour: content is visible by default
 * (never gets "stuck" invisible), and plays a one-shot rise animation the first time it enters
 * the viewport. Respects `prefers-reduced-motion`.
 */
export function Reveal({ children, as, delay = 0, className, style }: RevealProps) {
  const Tag = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Already on screen at first paint → stay visible without animating (avoids a flash).
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setAnimate(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={clsx(animate && "animate-rise", className)}
      style={delay ? { ...style, animationDelay: `${delay}ms` } : style}
    >
      {children}
    </Tag>
  );
}
